import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, DownloadOutline, HStack, rem, Spinner, Text, useToast } from '@bonusly/design-system';
import { useRecognizeURL } from '@bonusly/url';

import { LeaderboardFilters } from '../../../bonus-feed/features/recognition/api/hooks/leaderboards';

interface LeaderboardExportBarProps {
	totalCount: number;
	filters: Omit<LeaderboardFilters, 'limit' | 'offset'>;
	isFetching?: boolean;
}

function sanitizeSegment(str: string): string {
	return str
		.toLowerCase()
		.replace(/[^a-z0-9-]/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '');
}

const EXPORT_MAX_LIMIT = 50_000;

function buildExportFilename(filters: Omit<LeaderboardFilters, 'limit' | 'offset'>): string {
	const parts = ['leaderboard'];

	if (filters.hashtag) {
		parts.push(sanitizeSegment(filters.hashtag));
	} else if (filters.direction === 'given') {
		parts.push('top-givers');
	} else {
		parts.push('most-recognized');
	}

	if (filters.start_date && filters.end_date) {
		parts.push(`${filters.start_date}_to_${filters.end_date}`);
	}

	return parts.join('_') + '.csv';
}

export const LeaderboardExportBar: FC<LeaderboardExportBarProps> = ({ totalCount, filters, isFetching = false }) => {
	const { t } = useTranslation('navigation');
	const [isExporting, setIsExporting] = useState(false);
	const baseUrl = useRecognizeURL('api/v2/leaderboards/export_csv');
	const toast = useToast();
	const exportFilename = useMemo(() => buildExportFilename(filters), [filters]);

	const filtersRef = useRef(filters);
	filtersRef.current = filters;
	const filenameRef = useRef(exportFilename);
	filenameRef.current = exportFilename;
	const totalCountRef = useRef(totalCount);
	totalCountRef.current = totalCount;

	const handleExport = useCallback(async () => {
		const currentFilters = filtersRef.current;
		const currentFilename = filenameRef.current;
		const currentTotalCount = totalCountRef.current;
		setIsExporting(true);
		try {
			const params = new URLSearchParams();
			if (currentFilters.start_date) params.set('start_date', currentFilters.start_date);
			if (currentFilters.end_date) params.set('end_date', currentFilters.end_date);
			if (currentFilters.direction) params.set('direction', currentFilters.direction);
			if (currentFilters.hashtag) params.set('hashtag', currentFilters.hashtag);
			if (currentFilters.team) params.set('team', currentFilters.team);
			if (currentFilters.department) params.set('department', currentFilters.department);

			const url = `${baseUrl}?${params.toString()}`;
			const response = await fetch(url, { credentials: 'include' });

			if (!response.ok) throw new Error('Export failed');

			const blob = await response.blob();
			const downloadUrl = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.download = currentFilename;

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(downloadUrl);

			if (currentTotalCount > EXPORT_MAX_LIMIT) {
				toast({
					title: t('leaderboard.exportTruncated', { exported: EXPORT_MAX_LIMIT.toLocaleString(), total: currentTotalCount.toLocaleString() }),
					status: 'info',
					duration: 6000,
				});
			}
		} catch {
			toast({ title: t('leaderboard.exportError'), status: 'error', duration: 4000 });
		} finally {
			setIsExporting(false);
		}
	}, [baseUrl]);

	return (
		<Box>
			<Box mx={rem(-8)} borderTop="1px solid" borderColor="border.soft" />
			<HStack justify="space-between" align="center" px={rem(20)} py={rem(10)}>
				<Text fontSize="sm" color="text.softest">
					{t('leaderboard.resultCount', { count: totalCount })}
				</Text>
				<Button
					variant="ghost"
					colorScheme="primary"
					size="xs"
					onClick={handleExport}
					isDisabled={isExporting || isFetching}
					leftIcon={isExporting ? <Spinner size="xs" /> : <DownloadOutline boxSize={rem(16)} />}
				>
					{t('leaderboard.exportCsv')}
				</Button>
			</HStack>
		</Box>
	);
};
